import { useCallback, useRef, useState } from 'react';
import { useGetAlertsLazy } from '../../../API/hooks/alerts/useGetAlerts';
import { useGetTripsLazy } from '../../../API/hooks/trips/useGetTrips';
import { AlertDetails } from '../../../API/types/Alerts';
import { TripDetails } from '../../../API/types/Trips';
import { alertEventsMap } from '../../../pages/Alerts/constants/alertEventsMap';
import { getFormattedDateAndTime } from '../../../utils/dateUtils';
import { getAlertThumbnail } from '../../../pages/Alerts/helpers';
import { pageToQuery } from '../../../utils/pageIntoQueryParams';
import { buildQueryPageParam } from '../../../utils/queryFilters';
import { CalendarItemType } from './types';

const mapAlertsData = (item: AlertDetails): CalendarItemType => {
  const { shortDesc, fullDesc } = alertEventsMap[item?.name as string];
  return {
    _id: item._id,
    detailsId: item._id,
    type: 'alert',
    date: item.eventDate,
    thumbnailUrl: getAlertThumbnail(item),
    title: shortDesc,
    subtitle: fullDesc,
    vehicle: item.vehicle?.licensePlateNumber,
    updatedAt: item.updatedAt as string,
    riskLevel: item.riskLevel,
    alert: (item.lat && item.lon) ? {
      _id: item._id,
      lat: item.lat,
      lon: item.lon,
      name: item.name,
      riskLevel: item.riskLevel,
    } : undefined,
  };
};

const mapTripsData = (item: TripDetails): CalendarItemType[] => [{
  type: 'trip',
  _id: `${item._id}-start`,
  detailsId: (item.isMergedTrip && item.mergedTripId) ? item.mergedTripId : item._id,
  date: item.startAt,
  title: `Trip has started: ${item.startLocationName || 'N/A'}`,
  subtitle: getFormattedDateAndTime(item.startAt),
  vehicle: item.vehicle?.licensePlateNumber,
  updatedAt: item.updatedAt as string,
  isMergedTrip: !!item.isMergedTrip,
  tripPhase: 'start',
}, {
  type: 'trip',
  _id: `${item._id}-end`,
  detailsId: (item.isMergedTrip && item.mergedTripId) ? item.mergedTripId : item._id,
  date: item.endAt,
  title: `Trip has ended: ${item.endLocationName || 'N/A'}`,
  subtitle: getFormattedDateAndTime(item.endAt),
  vehicle: item.vehicle?.licensePlateNumber,
  updatedAt: item.updatedAt as string,
  isMergedTrip: !!item.isMergedTrip,
  tripPhase: 'end',
}];

const buildCalendarQueryPage = (
  baseQuery: string | undefined,
  page: number,
  sortDateField: 'eventDate' | 'startAt',
): string => {
  const { skip, limit } = pageToQuery(page);

  return buildQueryPageParam(baseQuery, skip, limit)
    .replaceAll('=createdAt', `=${sortDateField}`)
    .replaceAll('sortField=createdAt', `sortField=${sortDateField}`);
};

export function useGetCalendar(fleetId: string, payload?: any, query?: string, deps?: any): any {
  const {
    data: alertsData, isLoading: alertsLoading, isFetching: alertsIsFetching,
    fetchNextPage: alertsFetchNextPage, hasNextPage: alertsHasNextPage,
  } = useGetAlertsLazy(fleetId, {}, query ? query.replaceAll('=createdAt', '=eventDate') : query, deps);
  const {
    data: tripsData, isLoading: tripsLoading, isFetching: tripsIsFetching,
    fetchNextPage: tripsFetchNextPage, hasNextPage: tripsHasNextPage,
  } = useGetTripsLazy(fleetId, {}, query ? query.replaceAll('=createdAt', '=startAt') : query, deps);

  const mergedPages = [
    ...(alertsData?.pages?.map((page: any) => page?.results?.map(mapAlertsData)) || []),
    ...(tripsData?.pages?.map((page: any) => page?.results?.flatMap(mapTripsData)) || []),
  ].flat();

  const data = mergedPages.filter(
    (item) => !!item.date,
  ).sort(
    (a: CalendarItemType, b: CalendarItemType): number => (
      new Date(b.date).getTime() - new Date(a.date).getTime()
    ),
  );

  const [isPaginating, setIsPaginating] = useState(false);
  const paginatingRef = useRef(false);

  const fetchNextPage = useCallback(async (): Promise<void> => {
    if (paginatingRef.current || (!alertsHasNextPage && !tripsHasNextPage)) {
      return;
    }

    const fetches: Promise<unknown>[] = [];
    const alertsPages = alertsData?.pages?.length ?? 0;
    const tripsPages = tripsData?.pages?.length ?? 0;

    if (alertsHasNextPage) {
      fetches.push(alertsFetchNextPage({
        pageParam: buildCalendarQueryPage(query, alertsPages + 1, 'eventDate'),
      }));
    }
    if (tripsHasNextPage) {
      fetches.push(tripsFetchNextPage({
        pageParam: buildCalendarQueryPage(query, tripsPages + 1, 'startAt'),
      }));
    }

    if (fetches.length === 0) {
      return;
    }

    paginatingRef.current = true;
    setIsPaginating(true);
    try {
      await Promise.all(fetches);
    } finally {
      paginatingRef.current = false;
      setIsPaginating(false);
    }
  }, [
    alertsData?.pages?.length,
    alertsFetchNextPage,
    alertsHasNextPage,
    query,
    tripsData?.pages?.length,
    tripsFetchNextPage,
    tripsHasNextPage,
  ]);

  return {
    data: {
      pages: data,
    },
    pagesParams: alertsData?.pageParams.concat(tripsData?.pageParams),
    isLoading: alertsLoading || tripsLoading,
    isFetching: alertsIsFetching || tripsIsFetching,
    fetchNextPage,
    isFetchingNextPage: isPaginating,
    hasNextPage: alertsHasNextPage || tripsHasNextPage,
  };
}
